import {
    IDB_syncParamsActivityWorkflows,
    IDB_syncParamsFlowWorkflows,
    IDB_oldWorkflows,
    IDB_flowReports,
} from '../IDB/IDB.dexie'
import {
    IDB_workflowsDelete,
    IDB_workflowsReadAll,
    IDB_workflowsWrite,
    IDB_workflowReadSome,
} from '../IDB/IDB.table.workflow'
import { Serv_syncActivityWorkflows } from '../Serv/Serv.workflows'
import { Sync_standardSync, type Sync_Params } from '../Sync/Sync.standardSync'
import { hasVersion } from '../../../shared/domain/workflows/types.no-deps'
import { isAppActivityWorkflow, isFlowWorkflow, type Workflow } from '../../../app-core/lib/workflow/Workflow'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import type { Type_Workflow } from '../Type/Type.hive'
import { asyncDoNothing } from '../../../shared/lib/functional/function.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { SyncWorkflowsEndpoint } from '../../../server/client/client.no-deps'
import { Imag_preCacheFlowImages } from '../Imag/Imag.getUrls'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { filter, map } from '../../../shared/lib/functional/array.no-deps'
import { promiseThen } from '../../../shared/lib/functional/promiseThen.no-deps'
import { compact } from '../../../shared/lib/array.no-deps'
import { Cust_hasFeature } from '../Cust/Cust.hasFeature'
import type { ElementId } from '../../../shared/domain/flows'

export async function WF_sync(logger: ClientLogger) {
    logger.log(`syncing activity workflows`)

    // Activity workflows
    await Sync_standardSync({
        params: IDB_syncParamsActivityWorkflows(),
        sync: Serv_syncActivityWorkflows,
        write: (response) => IDB_workflowsWrite(logger, response.workflows),
        clear: clearAll(logger, isAppActivityWorkflow),
    })

    logger.log(`syncing flow workflows`)

    // Flow workflows
    await Sync_standardSync<SyncWorkflowsEndpoint['Output']>({
        params: IDB_syncParamsFlowWorkflows(),
        sync: (params) => syncFlowWorkflows(logger, params),
        write: (response) => updateVersionedFlowWorkflows(logger, response.workflows),
        // Do not need to clear flow workflows as only soft hidden
        clear: asyncDoNothing,
    })

    logger.log(`syncing workflows done`)
}

async function syncFlowWorkflows(logger: ClientLogger, params: Sync_Params) {
    const richText = Cust_hasFeature('workflow.richText-element')
    const nullDeletedFlows = Cust_hasFeature('workflows/sync.null-out-deleted')
    const response = await Serv_clientWithLogger(logger).syncFlowWorkflows(params, {
        includeUnitSubjectType: true,
        richText,
        nullDeletedFlows,
    })

    // Replace null flows in deleted workflows with an empty flow
    // So that checks like worfklow.flow == null still work
    for (const workflow of response.workflows) {
        workflow.flow = workflow.flow ?? {
            id: 'this is a fake flow' as ElementId,
            type: 'section',
            steps: [],
        }
    }

    await Imag_preCacheFlowImages(response.workflows)

    const count = response.workflows.length
    if (count > 0) {
        logger.log(`downloaded flows workflows`, { count })
    }

    return response
}

async function updateVersionedFlowWorkflows(logger: ClientLogger, newWorkflows: Type_Workflow[]) {
    logger.log(`new versioned workflows to update`, { count: newWorkflows.length })

    // Get the old versions workflows that have changed so we can preserve them
    // in the old workflows table
    const oldWorkflowVersions = await pipe(
        newWorkflows.filter(isFlowWorkflow),
        map((w) => w.id),
        IDB_workflowReadSome,
        promiseThen(
            compact,
            filter(isFlowWorkflow),
            filter((ow) => ow.numberOfVersions !== newWorkflows.find((nw) => nw.id === ow.id).numberOfVersions)
        )
    )

    await clearOldWorkflowVersions(logger)

    await IDB_oldWorkflows().bulkPut(oldWorkflowVersions)
    logger.log(`saved old workflows`, { count: oldWorkflowVersions.length })

    await IDB_workflowsWrite(logger, newWorkflows)
    logger.log(`saved new workflows`, { count: newWorkflows.length })
}

type IdVersionTuple = [number, number]

async function clearOldWorkflowVersions(logger: ClientLogger) {
    const reports = await IDB_flowReports().toArray()
    const activeWorkflowVersions = reports
        .map((report) => report.workflow)
        .filter(hasVersion)
        .map(({ id, version }) => [id, version] as IdVersionTuple)

    const count = await IDB_oldWorkflows().filter(workflowNotInUse(activeWorkflowVersions)).delete()

    logger.log(`deleted old workflows`, { count })
}

function workflowNotInUse(
    activeWorkflowVersions: IdVersionTuple[]
): (workfow: { id: number; numberOfVersions: number }) => boolean {
    return (workflow) =>
        // Because javascript doesn't have a proper tuple type and arrays cannot
        // be compared by value, we cannot use .includes, or any other nice
        // array methods
        activeWorkflowVersions.findIndex(
            (active) => active[0] === workflow.id && active[1] === workflow.numberOfVersions
        ) === -1
}

export function clearAll(logger: ClientLogger, filter: (workflow: Workflow) => boolean) {
    return async () => {
        logger.warn('deleting all workflows')
        const workflows = await IDB_workflowsReadAll()
        await IDB_workflowsDelete(
            logger,
            workflows.filter(filter).map((w) => w.id)
        )
    }
}
